import React, { useEffect, useMemo } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { MapPin, Check, Star, Clock, ShieldCheck, CalendarDays, Globe, Route } from 'lucide-react';
import Layout from '../components/Layout';
import { seoBySlug, seoKeywords, detectLang } from '../mock/seoKeywords';
import { seoExtra } from '../mock/seoExtra';
import { LANGS, LANG_META, isLang, translateKw, parseKw, routeCity, placeName, hashStr } from '../mock/seoLang';
import { A } from '../mock/seoContentA';
import { B } from '../mock/seoContentB';
import { routeFromZrh, coordsFor } from '../mock/content';
import useSEO from '../hooks/useSEO';
import { useApp } from '../context/AppContext';
import NotFound from './NotFound';
import RouteMap from '../components/RouteMap';
import WeatherBadge from '../components/WeatherBadge';

const AI = 'https://static.prod-images.emergentagent.com/jobs/33b17408-d0b5-4936-a469-8100a5ab05c3/images/';
const pool = [
  '4e291871d9e169b071f6ee98d86d387cb1dff676f97aa37b15f1cc70a7d240db.jpeg', 'ba3cab54b32d5ff34dcb2fabb9f7b343fccb3e2c62e4c9494df45fc86309ab50.jpeg',
  '7f95f1641140fb253fd05e2d07b2c55710090e3ecb1c9d125e2714187459a2c3.jpeg', '102349e4ffc48453bf1f02c17b4df5b70075155ac892bb5d9824ce460d05746a.jpeg',
  '112a5387e08207ec9fa9238dd8f9ddf197947b34c56b0e0d9d3fa1b362dfa836.jpeg', '8b53f17eafe43bd3990ae46fd6bfc2470d6faf7b9b74300d2d5eded4a732e3a2.jpeg',
  'd3f0bea74c54839bf360ce41cde052ad982a716d5afe2cd6935311b94e358c84.jpeg', '12fee0866b0dea31b06db8ec7695e89d975e8d6fde0edc2cf5317fa8471e317d.jpeg',
  '8741c4a56b833dcfe8f7f9f3266549feb52823d93c9fc9c2467286458b4081c3.jpeg', 'e1b4316aa66509bc8301aea95d01cc6f1fbde683db26331816bdd814cf882722.jpeg',
  'c19df19fa7b02a069c839e98c3c6d103110497a9898546b53c49a5205cc04295.jpeg', '624282150b31e76dd55cc47695314a0d09d83800e7e2aa4d173478a080909b9c.jpeg',
  '4a299fa6120b39f0adf4a0a8222bc7a54bcf70da597ab68eac23b74ea999dd7d.jpeg', 'd141e14c6be2435e5f092aa1932be8db36508e12b9c33162a24290a750fe6438.jpeg',
  'c36e4c8ec325eba4eefa6a63c6cda7c09b20d4e97b00dcf1a90c62792657a07c.jpeg', '5e5cfa0d948cbc2a98d6f96d431852a11b52761c20548b2d05cbe20a77b9a7ee.jpeg',
  '28ac247af65db7a59ce70f912a6410552ea7254beb7a60f184a7d0b4cbf37c65.jpeg', '000baaeec82298a8d638485d607c8b75eb8652c4f0fdffbc62ca67906e3f075d.jpeg',
  '1033bbaafc081d5283a903f13bb90421429d10f19da12b00c3a350147c21df94.jpeg', 'eaf669e485aaf9325d8328c50d76b4f82653b3b2073bfb216ba961ef2e1e54ff.jpeg',
];
// scenery matched to the destination named in the keyword
const SCENE = [
  [/lauterbrunnen|interlaken|grindelwald|wengen|m(ü|u)rren|jungfrau/, 10], [/lugano|locarno|ascona|bellinzona|ticino|tessin|mendrisio|chiasso/, 11],
  [/davos|klosters|wef/, 12], [/zermatt|saas|matterhorn|visp|brig/, 13], [/moritz|pontresina|samedan|engadin|scuol/, 14],
  [/gen(e|è)v|genf|ginevra|montreux|lausanne|vevey|nyon|morges/, 15], [/schaffhausen|rhein|rhine|konstanz|kreuzlingen/, 16], [/como|milan|mailand|milano|bergamo|malpensa|varese|stresa/, 17],
  [/lu(c|z)ern|luzern|weggis|vitznau|k(ü|u)ssnacht/, 18], [/arrival|ankunft|pickup|abholung|meet|hb|zrh\b|kloten|airport|flughafen|a(é|e)roport|aeroporto|aeropuerto|مطار|аэропорт|havaliman/, 19],
];
const pickImg = (kw, h) => {
  const k = kw.toLowerCase();
  const m = SCENE.find(([re]) => re.test(k));
  return AI + pool[m ? m[1] : h % 10];
};
const EXTRA_LANGS = ['en', 'de', 'fr', 'it', 'es', 'ar', 'ru', 'tr'];
const ZRH_LABEL = 'Flughafen Zürich (ZRH)';

const KeywordPage = () => {
  const { slug, lang: langParam } = useParams();
  const { kwReady, setWaText } = useApp();
  const entry = seoBySlug(slug);
  const baseLang = entry ? detectLang(entry.kw) : 'en';
  const lang = langParam && isLang(langParam) ? langParam : baseLang;
  const c = A[lang] || B[lang];
  const h = entry ? hashStr(`${slug}|${lang}`) : 0;

  const data = useMemo(() => {
    if (!entry) return null;
    const title = lang === baseLang ? entry.kw : translateKw(entry.kw, lang);
    const cityRaw = routeCity(entry.kw);
    const route = routeFromZrh(cityRaw);
    const city = cityRaw ? placeName(cityRaw, lang) : '';
    const o = parseKw(entry.kw);
    const toZrh = o.to.some((p) => p.c === 'ZRH');
    const cityEn = cityRaw ? placeName(cityRaw, 'en') : '';
    const from = toZrh ? cityEn : ZRH_LABEL;
    const to = toZrh ? ZRH_LABEL : cityEn;
    const start = seoKeywords.length > 13 ? h % (seoKeywords.length - 13) : 0;
    const related = seoKeywords.slice(start, start + 12).filter((k) => k.slug !== slug);
    return { title, city, route, from, to, related, img: pickImg(entry.kw, h), faqs: c.faq(title), coords: coordsFor(cityRaw) };
  }, [entry, lang, baseLang, h, slug, c]);

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const urlFor = (l) => (l === baseLang ? `${origin}/t/${slug}` : `${origin}/${l}/t/${slug}`);
  const seoOpts = useMemo(() => entry ? {
    canonical: urlFor(lang),
    lang, dir: LANG_META[lang].dir,
    alternates: [...LANGS.map((l) => ({ lang: l, href: urlFor(l) })), { lang: 'x-default', href: `${origin}/t/${slug}` }],
    noindex: false,
  } : { skip: true },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [entry, origin, lang, baseLang, slug]);

  const intro = data && c.intro[h % c.intro.length](data.title);
  useSEO(data ? `${data.title} | CAB.TAXI` : 'CAB.TAXI', intro ? intro.slice(0, 155) : '', seoOpts);

  const faqs = data ? data.faqs : [];
  useEffect(() => {
    if (!entry) return;
    const key = `pv:${slug}:${lang}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track/view`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ slug, lang: langParam ? lang : 'orig' }) }).catch(() => {});
  }, [entry, slug, lang, langParam]);
  useEffect(() => {
    if (!data) return undefined;
    const s = document.createElement('script');
    s.type = 'application/ld+json';
    s.setAttribute('data-seo-ld', '1');
    s.text = JSON.stringify({ '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: data.faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) });
    document.head.appendChild(s);
    return () => s.remove();
  }, [data]);
  const routeText = data ? (data.to && data.from !== data.to ? `${data.from} → ${data.to}` : data.title) : '';
  useEffect(() => {
    if (!data) return undefined;
    setWaText(routeText);
    return () => setWaText('');
  }, [data, routeText, setWaText]);

  if (!entry && !kwReady) return <Layout hideBooking><div className="min-h-[40vh]" data-testid="keyword-loading" /></Layout>;
  if (!entry) return <NotFound />;
  if (langParam && !isLang(langParam)) return <Navigate to={`/t/${slug}`} replace />;
  if (langParam && langParam === baseLang) return <Navigate to={`/t/${slug}`} replace />;

  const rtl = LANG_META[lang].dir === 'rtl';
  const svc = c.svc[((h >>> 1) % c.svc.length)]();
  const reg = c.reg[((h >>> 2) % c.reg.length)]();
  const body = ((h >>> 3) % 2) ? [svc, reg] : [reg, svc];
  const more = EXTRA_LANGS.includes(lang) ? seoExtra(lang).more(data.title) : [];
  const bookHref = `/booking?from=${encodeURIComponent(data.from)}&to=${encodeURIComponent(data.to)}&src=${encodeURIComponent(slug)}`;
  const prefix = `/${lang}`;

  return (
    <Layout booking={{ from: data.from, to: data.to, src: slug }}>
      <section className="relative">
        <img src={data.img} alt={data.title} className="w-full h-[40vh] min-h-[300px] object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/20" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 w-full pb-8" dir={rtl ? 'rtl' : 'ltr'}>
            <div className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-4 py-1.5 text-xs font-semibold text-white mb-3">
              <MapPin className="w-3.5 h-3.5" style={{ color: 'var(--brand-accent)' }} /> Zürich · CAB.TAXI · {LANG_META[lang].name}
            </div>
            <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight max-w-4xl" data-testid="keyword-title" data-no-translate>{data.title}</h1>
            {data.title !== entry.kw && <p className="mt-2 text-white/70 text-sm" data-testid="keyword-original"><span className="font-semibold">{c.also}:</span> {entry.kw}</p>}
          </div>
        </div>
      </section>

      <section className="py-12" dir={rtl ? 'rtl' : 'ltr'}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 grid lg:grid-cols-[1fr_300px] gap-10 items-start">
          <article className="space-y-5" data-testid="keyword-article" data-no-translate>
            <p className="text-gray-700 leading-relaxed text-[15px]">{intro}</p>
            {data.route && (
              <div className="rounded-2xl border border-gray-100 bg-[color:var(--brand-muted)] p-5" data-testid="keyword-route">
                <div className="flex items-center gap-2 font-extrabold text-[color:var(--brand-dark)] mb-3"><Route className="w-4 h-4" style={{ color: 'var(--brand-accent)' }} /> {c.routeLbl}: {ZRH_LABEL} → {data.city}</div>
                <div className="grid grid-cols-3 gap-3 text-center" dir="ltr">
                  <div><div className="text-xs text-gray-500">{c.kmLbl}</div><div className="font-extrabold text-lg" data-testid="keyword-km">{data.route.km} km</div></div>
                  <div><div className="text-xs text-gray-500">{c.minLbl}</div><div className="font-extrabold text-lg">~{data.route.mins} min</div></div>
                  <div><div className="text-xs text-gray-500">{c.priceLbl}</div><div className="font-extrabold text-lg" style={{ color: 'var(--brand-accent)' }} data-testid="keyword-price">CHF {data.route.price}</div></div>
                </div>
                <p className="mt-3 text-sm text-gray-600 leading-relaxed">{c.dist(data.city, data.route.km, data.route.mins, data.route.price)}</p>
                <div className="mt-4 grid sm:grid-cols-[1fr_260px] gap-3 items-start" dir="ltr"><RouteMap from={data.from} to={data.to} /><WeatherBadge coords={data.coords} city={data.city} lang={lang} bookHref={bookHref} /></div>
              </div>
            )}
            {body.map((p, i) => <p key={i} className="text-gray-600 leading-relaxed text-[15px]">{p}</p>)}
            {more.map((p, i) => <p key={`m${i}`} className="text-gray-600 leading-relaxed text-[15px]">{p}</p>)}
            <div className="pt-2">
              <h2 className="text-xl font-extrabold text-[color:var(--brand-dark)] mb-3">{c.why}</h2>
              <ul className="grid sm:grid-cols-2 gap-2">
                {c.feats.map((f) => <li key={f} className="flex items-center gap-2 text-sm text-[color:var(--brand-dark)]"><Check className="w-4 h-4 shrink-0" style={{ color: 'var(--brand-accent)' }} /> {f}</li>)}
              </ul>
            </div>
            <p className="text-gray-600 leading-relaxed text-[15px]">{c.book[0]()}</p>
            <div className="pt-2" data-testid="keyword-faq">
              <h2 className="text-xl font-extrabold text-[color:var(--brand-dark)] mb-3">{c.faqTitle}</h2>
              <div className="space-y-3">
                {faqs.map((f) => (
                  <div key={f.q} className="rounded-xl border border-gray-100 bg-white p-4">
                    <div className="font-bold text-[color:var(--brand-dark)]">{f.q}</div>
                    <p className="mt-1.5 text-sm text-gray-600 leading-relaxed">{f.a}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="pt-6" data-testid="keyword-related">
              <div className="flex flex-wrap gap-2">
                {data.related.map((r) => (
                  <Link key={r.slug} to={lang === detectLang(r.kw) ? `/t/${r.slug}` : `${prefix}/t/${r.slug}`} className="text-xs font-semibold text-[color:var(--brand-dark)] bg-[color:var(--brand-muted)] rounded-full px-3 py-1.5 hover:bg-gray-200" data-testid={`kw-related-${r.slug}`}>{translateKw(r.kw, lang)}</Link>
                ))}
              </div>
            </div>
            <div className="pt-6 border-t border-gray-100" data-testid="keyword-languages">
              <div className="flex items-center gap-2 text-sm font-bold text-[color:var(--brand-dark)] mb-3"><Globe className="w-4 h-4" style={{ color: 'var(--brand-accent)' }} /> {c.otherLangs}</div>
              <div className="flex flex-wrap gap-2" dir="ltr">
                {LANGS.map((l) => (
                  <Link key={l} to={l === baseLang ? `/t/${slug}` : `/${l}/t/${slug}`} hrefLang={l} className={`text-xs font-semibold rounded-full px-3 py-1.5 border ${l === lang ? 'text-white border-transparent' : 'text-[color:var(--brand-dark)] border-gray-200 hover:bg-gray-50'}`} style={l === lang ? { background: 'var(--brand-dark)' } : undefined} data-testid={`kw-lang-${l}`}>{LANG_META[l].name}</Link>
                ))}
              </div>
            </div>
          </article>

          <aside className="rounded-2xl bg-[color:var(--brand-muted)] p-6 lg:sticky lg:top-24" dir="ltr" data-testid="keyword-booking">
            <div className="flex items-center gap-1 mb-2">{[1, 2, 3, 4, 5].map((i) => <Star key={i} className="w-4 h-4 fill-current" style={{ color: 'var(--brand-accent)' }} />)}<span className="text-xs text-gray-500 ml-1">4.8/5</span></div>
            {data.route && <div className="text-2xl font-extrabold text-[color:var(--brand-dark)]">{c.priceLbl} CHF {data.route.price}</div>}
            <ul className="space-y-2 my-4">
              <li className="flex items-center gap-2 text-sm text-[color:var(--brand-dark)]"><ShieldCheck className="w-4 h-4" style={{ color: 'var(--brand-accent)' }} /> {c.feats[0]}</li>
              <li className="flex items-center gap-2 text-sm text-[color:var(--brand-dark)]"><Clock className="w-4 h-4" style={{ color: 'var(--brand-accent)' }} /> 24/7</li>
            </ul>
            <Link to={bookHref} className="accent-btn w-full rounded-xl py-3 font-bold flex items-center justify-center gap-2" data-testid="keyword-book-btn"><CalendarDays className="w-4 h-4" /> {c.btn}</Link>
          </aside>
        </div>
      </section>
    </Layout>
  );
};

export default KeywordPage;
